import { isEmpty, keyBy, merge, uniqBy, values } from "lodash";
import { getCurrentJobWorkHistory, getNameInitials, snakeCaseToCapitalizedString } from "utils";

// Types
import {
    ProjectDataResponse,
    ProjectDataChildCompanySetList,
    ProjectDataChildPeopleSetList,
    ProjectDataChildSearchAIList,
    ProjectDataChildInsight,
    ProjectDataChildActivityLogList,
    ProjectDataChildBriefSetList,
    ProjectDataCompany,
    ProjectDataPerson,
    ProjectDataChildProjectCompany,
    searchAIChildBrief,
    searchAIChildSearchCompanies,
    searchAIChildPersonA,
    searchAIChildPersonB,
    searchAIAllChildren,
    InsightChildCompanyRef,
    InsightChildPersonRef,
    InsightChildVCP,
    InsightChildSearchPeers,
    searchAIChildSearchLeaders,
    ProjectPersonSet,
    ProjectDataPersonMeta,
    InsightData,
    BriefChildren,
    ProjectSearchAICreationResponse,
    BriefChildInsightRef,
    ProjectChunks,
    ProjectDataChildPeers,
    ProjectDataChildProjectGroup,
} from "services/projects/project.types";
import camelcaseKeys from "camelcase-keys";
import { compareDesc } from "date-fns";
import { flattenSectorProductsDomain } from "services/company/company.functions";
import { AnyAction } from "@reduxjs/toolkit";
import { DraxRole } from "store/slice/Team";

export type TConvertedPeopleList = ReturnType<typeof convertProjectPerson>;
export type TConvertedCompaniesList = ReturnType<typeof convertProjectCompany>;

// ======== UTILS ===================================================================================

export const extractIdFromPath = (path: string) => Number(path.split("/").pop()?.replace(/\D/g, ""));

// Company Utils
export const getProjectCompany = (projectData: ProjectDataResponse | undefined) =>
    projectData?.children?.find((c) => c.meta.type === "PROJECT_COMPANY") as ProjectDataChildProjectCompany | undefined;

// Insights Utils
export const getProjectInsightsChildren = (projectData: ProjectDataResponse | undefined) =>
    (projectData?.children?.find((c) => c.meta.type === "PROJECT_INSIGHT_LIST") as ProjectDataChildInsight | undefined)
        ?.children;

export const getInsightFilters = (insightData: InsightData | undefined) => {
    return insightData?.children?.find((child) => child.meta.type === "PROJECT_SEARCH_PEERS") as
        | InsightChildSearchPeers
        | undefined;
};

export const getInsightPeersPath = (insightData: InsightData | undefined) => {
    return insightData?.children?.find((child) => child.meta.type === "PROJECT_COMPANY_SET_REF") as
        | InsightChildCompanyRef
        | undefined;
};

export const getInsightRelatedSltPath = (insightData: InsightData | undefined) => {
    return insightData?.children?.find((child) => child.meta.type === "PROJECT_PERSON_SET_REF") as
        | InsightChildPersonRef
        | undefined;
};

export const getInsightRelatedVcpData = (insightData: InsightData | undefined) => {
    return insightData?.children?.find((child) => child.meta.type === "PROJECT_VCP") as InsightChildVCP | undefined;
};

export const getInsightRelatedSlt = (
    projectData: ProjectDataResponse | undefined,
    insightId: number | string | string[] | qs.ParsedQs | qs.ParsedQs[] | undefined | null
) => {
    const projectId = projectData?.data?.id;
    return getProjectInsightsChildren(projectData)
        ?.find((c) => c.meta.path === `/project_${projectId}/insight_list/insight_${insightId}`)
        ?.children?.find((c) => c.meta.path === `/project_${projectId}/insight_list/insight_${insightId}/slt`) as
        | InsightChildPersonRef
        | undefined;
};

// Briefs Utils
export const getProjectBriefsChildren = (projectData: ProjectDataResponse | undefined) =>
    (
        projectData?.children?.find((c) => c.meta.type === "PROJECT_BRIEF_LIST") as
            | ProjectDataChildBriefSetList
            | undefined
    )?.children;

export const getBriefRelatedInsight = (briefData: BriefChildren | undefined) => {
    return briefData?.children?.find((child) => child.meta.type === "PROJECT_INSIGHT_REF") as
        | BriefChildInsightRef
        | undefined;
};

// SearchAIs Utils
export const getProjectSearchAIsChildren = (projectData: ProjectDataResponse | undefined) =>
    (
        projectData?.children?.find((c) => c.meta.type === "PROJECT_SEARCH_AI_LIST") as
            | ProjectDataChildSearchAIList
            | undefined
    )?.children;

export const getSearchAiCompaniesFilters = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    return searchAiData?.children?.find((child) => child.meta.type === "PROJECT_SEARCH_COMPANIES") as
        | searchAIChildSearchCompanies
        | undefined;
};

export const getSearchAiPeers = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    const searchAiFilters = getSearchAiCompaniesFilters(searchAiData);
    return searchAiFilters?.children?.find((child) => child.meta.type === "PROJECT_COMPANY_SET_REF_A");
};

export const getSearchAiCompaniesLonglist = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    const searchAiFilters = getSearchAiCompaniesFilters(searchAiData);
    return searchAiFilters?.children?.find((child) => child.meta.type === "PROJECT_COMPANY_SET_REF_B");
};

export const getSearchAiLeadersFilters = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    return searchAiData?.children?.find((child) => child.meta.type === "PROJECT_SEARCH_LEADERS") as
        | searchAIChildSearchLeaders
        | undefined;
};

export const getSearchAiLeadersLongList = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    return searchAiData?.children?.find((child) => child.meta.type === "PROJECT_PERSON_SET_REF_A") as
        | searchAIChildPersonA
        | undefined;
};

export const getSearchAiLeadersShortList = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    return searchAiData?.children?.find((child) => child.meta.type === "PROJECT_PERSON_SET_REF_B") as
        | searchAIChildPersonB
        | undefined;
};

export const getProjectSearchAiBrief = (searchAiData: ProjectSearchAICreationResponse | undefined) => {
    return searchAiData?.children?.find((searchAi) => searchAi.meta.type === "PROJECT_BRIEF_REF") as
        | searchAIChildBrief
        | undefined;
};

export const getSearchAiRelatedPersonSetA = (
    projectData: ProjectDataResponse | undefined,
    searchAiId: number | string | string[] | qs.ParsedQs | qs.ParsedQs[] | undefined | null
) => {
    const projectId = projectData?.data?.id;
    return getProjectSearchAIsChildren(projectData)
        ?.find((c) => c.meta.path === `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`)
        ?.children?.find((c) => c.meta.type === "PROJECT_PERSON_SET_REF_A") as searchAIChildPersonA | undefined;
};

export const getSearchAiRelatedPersonSetB = (
    projectData: ProjectDataResponse | undefined,
    searchAiId: number | string | string[] | qs.ParsedQs | qs.ParsedQs[] | undefined | null
) => {
    const projectId = projectData?.data?.id;
    return getProjectSearchAIsChildren(projectData)
        ?.find((c) => c.meta.path === `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`)
        ?.children?.find((c) => c.meta.type === "PROJECT_PERSON_SET_REF_B") as searchAIChildPersonB | undefined;
};

export const getSearchAiRelatedCompanies = (
    projectData: ProjectDataResponse | undefined,
    searchAiId: number | string | string[] | qs.ParsedQs | qs.ParsedQs[] | undefined | null
) => {
    const projectId = projectData?.data?.id;
    return getProjectSearchAIsChildren(projectData)
        ?.find((c) => c.meta.path === `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`)
        ?.children?.find((c) => c.meta.type === "PROJECT_SEARCH_COMPANIES") as searchAIChildSearchCompanies | undefined;
};

// People Set Utils
export const getProjectPersonSetChildren = (projectData: ProjectDataResponse | undefined) =>
    (
        projectData?.children?.find((c) => c.meta.type === "PROJECT_PERSON_SET_LIST") as
            | ProjectDataChildPeopleSetList
            | undefined
    )?.children;

// Companies Set Utils
export const getProjectCompanySetChildren = (projectData: ProjectDataResponse | undefined) =>
    (
        projectData?.children?.find((c) => c.meta.type === "PROJECT_COMPANY_SET_LIST") as
            | ProjectDataChildCompanySetList
            | undefined
    )?.children;

// Utils Person List
export const getPersonListByPath = (projectData: ProjectDataResponse | undefined, path: string | undefined) => {
    return (
        projectData?.children?.find((c) => c.meta.type === "PROJECT_PERSON_SET_LIST") as ProjectDataChildPeopleSetList
    )?.children?.find((c) => c.meta.path === path);
};

export const getPersonMetaUI = (
    list: ProjectPersonSet,
    id: number
): ProjectDataPersonMeta | Record<string, unknown> => {
    const listIds = list?.meta_ui?.person_ids;
    if (listIds?.length) {
        const personMetaUI = listIds.find((p) => p.id === id);
        if (personMetaUI) {
            return personMetaUI;
        }
    }
    return {};
};

// ======================== NEW FUNCTIONS ====================================================

export type TInitialFetch = {
    company?: ReturnType<typeof flattenSectorProductsDomain> | undefined;
    insight?: InsightData;
    brief?: BriefChildren;
    searchAi?: ProjectSearchAICreationResponse;
    roles?: DraxRole[];
};

export type TInitialFetchError = {
    error: true;
    reason: any;
};

export const getInitialFetchResponse = (
    data: PromiseSettledResult<AnyAction>[]
): TInitialFetch | TInitialFetchError => {
    let response = {} as TInitialFetch;

    for (const result of data) {
        if (result.status === "rejected") {
            return {
                error: true,
                reason: result.reason,
            };
        }

        if (result.status === "fulfilled") {
            if (result.value.type === `project/fetchCompanyData/fulfilled`) {
                const company = result.value.payload as ReturnType<typeof flattenSectorProductsDomain>;
                response = { ...response, company };
            }

            if (result.value.type === `project/insights/getInsight/fulfilled`) {
                const insight = result.value.payload as InsightData;
                response = { ...response, insight };
            }

            if (result.value.type === `project/briefs/getBrief/fulfilled`) {
                const brief = result.value.payload as BriefChildren;
                response = { ...response, brief };
            }

            if (result.value.type === `project/serachAis/getSearchAi/fulfilled`) {
                const searchAi = result.value.payload as ProjectSearchAICreationResponse;
                response = { ...response, searchAi };
            }

            if (result.value.type === `team/roles/fetch/fulfilled`) {
                const roles = result.value.payload as DraxRole[];
                response = { ...response, roles };
            }
        }
    }

    return response;
};

export const getListsFetchResponse = <T>(data: PromiseSettledResult<AnyAction>[]) => {
    let response: T[] | TInitialFetchError = [];

    for (const result of data) {
        if (result.status === "rejected") {
            response = {
                error: true,
                reason: result.reason,
            };
            break;
        }

        if (result.status === "fulfilled") {
            const res = result.value.payload;
            if (!res) {
                response = {
                    error: true,
                    reason: result.value.meta.requestStatus,
                };
                break;
            }

            const list = res as T;
            response = [...(response as unknown as T[]), list];
        }
    }

    return response;
};

export const projectChunksToProjectData = (projectData: ProjectDataResponse, chunks: ProjectChunks) => {
    const company = projectData.children?.find((child) => child.meta.type === "PROJECT_COMPANY");
    const group = projectData.children?.find((child) => child.meta.type === "PROJECT_GROUP");
    const searchPeers = projectData.children?.find((child) => child.meta.type === "PROJECT_SEARCH_PEERS");
    const logList = projectData.children?.find((child) => child.meta.type === "PROJECT_ACTIVITY_LOG_LIST");
    const insights = projectData.children?.find((child) => child.meta.type === "PROJECT_INSIGHT_LIST") as
        | ProjectDataChildInsight
        | undefined;
    const briefs = projectData.children?.find((child) => child.meta.type === "PROJECT_BRIEF_LIST") as
        | ProjectDataChildBriefSetList
        | undefined;
    const searchAis = projectData.children?.find((child) => child.meta.type === "PROJECT_SEARCH_AI_LIST") as
        | ProjectDataChildSearchAIList
        | undefined;
    const companySet = projectData.children?.find((child) => child.meta.type === "PROJECT_COMPANY_SET_LIST") as
        | ProjectDataChildCompanySetList
        | undefined;
    const peopleSet = projectData.children?.find((child) => child.meta.type === "PROJECT_PERSON_SET_LIST") as
        | ProjectDataChildPeopleSetList
        | undefined;

    const newInsights = values(merge(keyBy(chunks.insights, "data.id"), keyBy(insights?.children || [], "data.id")));
    const newSearchAIs = values(merge(keyBy(chunks.searchAIs, "data.id"), keyBy(searchAis?.children || [], "data.id")));
    const newBriefs = values(merge(keyBy(chunks.briefs, "data.id"), keyBy(briefs?.children || [], "data.id")));
    const newPeopleSet = values(
        merge(keyBy(chunks.personSets, "data.id"), keyBy(peopleSet?.children || [], "data.id"))
    );
    const newCompanySet = values(
        merge(keyBy(chunks.companySets, "data.id"), keyBy(companySet?.children || [], "data.id"))
    );

    return {
        ...projectData,
        children: [
            {
                meta: insights?.meta,
                children: newInsights,
            } as ProjectDataChildInsight,
            {
                meta: briefs?.meta,
                children: newBriefs,
            } as ProjectDataChildBriefSetList,
            {
                meta: searchAis?.meta,
                children: newSearchAIs,
            } as ProjectDataChildSearchAIList,
            {
                meta: peopleSet?.meta,
                children: newPeopleSet,
            } as ProjectDataChildPeopleSetList,
            {
                meta: companySet?.meta,
                children: newCompanySet,
            } as ProjectDataChildCompanySetList,
            { ...(company as ProjectDataChildProjectCompany) },
            { ...(group as ProjectDataChildProjectGroup) },
            { ...(searchPeers as ProjectDataChildPeers) },
            { ...(logList as ProjectDataChildActivityLogList) },
        ],
    } as ProjectDataResponse;
};

// ===========================================================================================

export enum SnackBarEntityTypes {
    company = "company",
    people = "people",
    shortlist = "shortlist",
}

export const convertCompanyData = (data: ReturnType<typeof flattenSectorProductsDomain> | undefined) => {
    if (isEmpty(data)) {
        return undefined;
    }

    return {
        ...data,
        products: {
            name: "Products",
            icon: "product" as const,
            items: data?.products.map((p) => ({ key: p.id, value: p.name, selected: true })),
        },
        sectors: {
            name: "Sectors",
            icon: "sector" as const,
            items: data?.sectors.map((s) => ({ key: s.id, value: s.name, selected: true })),
        },
        capitalStructure: {
            name: "Capital Structure",
            icon: "pie" as const,
            items: data?.transactions.map((t, i) => ({
                key: i,
                value: snakeCaseToCapitalizedString(t.capital_structure || ""),
                selected: true,
            })),
        },
        transactions: {
            name: "Transactions",
            icon: "deals" as const,
            items:
                data?.transactions
                    ?.map((trx) => ({
                        key: trx.id,
                        date: trx.date,
                        deal: snakeCaseToCapitalizedString(trx.capital_structure || ""),
                        transactionTypes:
                            trx.transaction_types?.map((type) => ({
                                id: type.transaction_type.id,
                                transactionName: snakeCaseToCapitalizedString(type.transaction_type.name),
                            })) || [],
                    }))
                    .sort((a, b) => compareDesc(new Date(a.date || ""), new Date(b.date || ""))) || [],
        },
    };
};

export const convertProjectPerson = (data: ProjectDataPerson) => {
    const currentJobData = getCurrentJobWorkHistory(data.work_history);
    return camelcaseKeys({
        ...data,
        company: currentJobData?.company_name?.toUpperCase(),
        jobTitle: currentJobData?.work_position_name?.toUpperCase(),
    });
};

export const convertProjectCompany = (data: ProjectDataCompany) =>
    camelcaseKeys({
        ...data,
        capitalStructure: data.current_capital_structure,
    });

export const getCompaniesList = (data: ProjectDataResponse) => {
    if (isEmpty(data)) {
        return [];
    }

    const companiesListData = data.children.find((child) => child?.meta?.type === "PROJECT_COMPANY_SET_LIST") as
        | ProjectDataChildCompanySetList
        | undefined;

    return (
        companiesListData?.children?.map((company) => {
            return {
                id: company.data.id,
                name: company.data.name || "",
                ref: company.meta.path,
                shouldHighlight: false,
                shouldHide: false,
                isClone: company.data.is_clone,
                createdBy: getNameInitials(company.avatar?.name || "").slice(0, 2),
                count: company.meta.children_count,
                companies: [] as unknown as TConvertedCompaniesList[],
                activityLog: company?.avatar_history?.map((log) => ({
                    id: log.id,
                    name: log.name,
                    datetime_utc: log.datetime_utc,
                    activity: log.activity,
                })),
            };
        }) || []
    );
};

export const getPeopleList = (data: ProjectDataResponse) => {
    if (isEmpty(data)) {
        return [];
    }

    const peopleListData = data.children.find((child) => child?.meta?.type === "PROJECT_PERSON_SET_LIST") as
        | ProjectDataChildPeopleSetList
        | undefined;

    return (
        peopleListData?.children
            ?.map((person) => {
                return {
                    id: person.data.id,
                    name: person.data.name,
                    ref: person.meta.path,
                    shouldHighlight: false,
                    shouldHide: false,
                    isClone: null,
                    createdBy: getNameInitials(person.avatar?.name || "").slice(0, 2),
                    count: person.meta.children_count,
                    people: [] as unknown as TConvertedPeopleList[],
                    activityLog: person?.avatar_history?.map((log) => ({
                        id: log.id,
                        name: log.name,
                        datetime_utc: log.datetime_utc,
                        activity: log.activity,
                    })),
                };
            })
            .sort((a, b) => a.name?.localeCompare(b.name, "es", { sensitivity: "base" })) || []
    );
};

export const getInsightsList = (data: ProjectDataResponse) => {
    if (isEmpty(data)) {
        return [];
    }

    const targetCompanyId = (
        data.children.find((child) => child.meta.type === "PROJECT_COMPANY") as
            | ProjectDataChildProjectCompany
            | undefined
    )?.data?.id;

    const insightList = data?.children?.find((child) => child.meta.type === "PROJECT_INSIGHT_LIST") as
        | ProjectDataChildInsight
        | undefined;

    return (
        insightList?.children?.map((insight) => {
            const peers = (
                insight?.children?.find((l) => l.meta.type === "PROJECT_COMPANY_SET_REF") as
                    | InsightChildCompanyRef
                    | undefined
            )?.ref?.path;
            const slt = (
                insight?.children?.find((l) => l.meta.type === "PROJECT_PERSON_SET_REF") as
                    | InsightChildPersonRef
                    | undefined
            )?.ref?.path;

            const relatedSLTRef = getInsightRelatedSltPath(insight)?.ref?.path;
            const sltCount = getPersonListByPath(data, relatedSLTRef)?.data.person_ids.length || 0;

            const relatedPeers = (
                data.children.find((child) => child.meta.type === "PROJECT_COMPANY_SET_LIST") as
                    | ProjectDataChildCompanySetList
                    | undefined
            )?.children?.find((c) => c.meta.path === peers);

            const relatedSlt = (
                data.children.find((child) => child.meta.type === "PROJECT_PERSON_SET_LIST") as
                    | ProjectDataChildPeopleSetList
                    | undefined
            )?.children?.find((c) => c.meta.path === slt);

            const createdOn = insight?.avatar_history?.find((ah) => ah.activity === "Created");

            const insightHistory = insight?.avatar_history || [];
            const insightPeersHistory = relatedPeers?.avatar_history || [];
            const insightSltHistory = relatedSlt?.avatar_history || [];
            const allHistory = [...insightHistory, ...insightPeersHistory, ...insightSltHistory];

            return {
                id: insight.data.id,
                name: insight.data.name,
                shouldHighlight: false,
                shouldHide: false,
                isDisabled: false,
                activityLog: insight?.avatar_history || [],
                meta: {
                    createdOn,
                    lastUpdatedOn: allHistory.sort((a, b) =>
                        compareDesc(new Date(a.datetime_utc || ""), new Date(b.datetime_utc || ""))
                    )[0],
                    collaborators:
                        uniqBy(
                            allHistory?.filter((ah) => ah.name !== createdOn?.name),
                            "name"
                        ) || [],
                    projectId: data.data.id,
                    ref: data.meta.path,
                    companyId: targetCompanyId,
                    peers: peers && extractIdFromPath(peers),
                    slt: slt && extractIdFromPath(slt),
                    sltCount,
                    vcp: (
                        insight?.children?.find((log) => log.meta.type === "PROJECT_VCP") as InsightChildVCP | undefined
                    )?.data?.id,
                },
            };
        }) || []
    );
};

export const getBriefList = (data: ProjectDataResponse) => {
    if (isEmpty(data)) {
        return [];
    }

    const briefList = data.children.find((child) => child.meta.type === "PROJECT_BRIEF_LIST") as
        | ProjectDataChildBriefSetList
        | undefined;

    return (
        briefList?.children
            ?.filter((d) => d.avatar)
            ?.map((d) => {
                const insightRef = (
                    d.children?.find((l) => l.meta.type === "PROJECT_INSIGHT_REF") as
                        | ProjectDataChildBriefSetList["children"][0]["children"][0]
                        | undefined
                )?.ref?.path;
                const relatedInsight = (
                    data.children?.find((child) => child.meta.type === "PROJECT_INSIGHT_LIST") as
                        | ProjectDataChildInsight
                        | undefined
                )?.children?.find((insight) => insight.meta.path === insightRef)?.data;

                const searchAIs = (
                    data.children?.find((child) => child.meta.type === "PROJECT_SEARCH_AI_LIST") as
                        | ProjectDataChildSearchAIList
                        | undefined
                )?.children
                    ?.filter((sa) => sa.meta.type === "PROJECT_SEARCH_AI")
                    .filter((sa) => {
                        return sa.children.find(
                            (e) =>
                                e.meta.type === "PROJECT_BRIEF_REF" &&
                                (e as searchAIChildBrief).ref?.path.includes(`brief_${d.data.id}`)
                        );
                    });

                const relatedLists = searchAIs?.map((sa) => {
                    const peopleListRef = (
                        sa?.children?.find((e) => e.meta?.type === "PROJECT_PERSON_SET_REF_A") as
                            | searchAIChildPersonA
                            | undefined
                    )?.ref?.path;

                    const shortListRef = (
                        sa?.children?.find((e) => e.meta?.type === "PROJECT_PERSON_SET_REF_B") as
                            | searchAIChildPersonB
                            | undefined
                    )?.ref?.path;

                    const companiesRef = (
                        sa?.children?.find((e) => e.meta.type === "PROJECT_SEARCH_COMPANIES") as
                            | searchAIChildSearchCompanies
                            | undefined
                    )?.children?.find((c) => c.meta.type === "PROJECT_COMPANY_SET_REF_B")?.ref?.path;

                    const company = (
                        data.children?.find((c) => c.meta.type === "PROJECT_COMPANY_SET_LIST") as
                            | ProjectDataChildCompanySetList
                            | undefined
                    )?.children?.find((c) => c.meta.path === companiesRef);

                    const people = (
                        data.children?.find((c) => c.meta.type === "PROJECT_PERSON_SET_LIST") as
                            | ProjectDataChildPeopleSetList
                            | undefined
                    )?.children?.find((c) => c.meta.path === peopleListRef);

                    const shortList = (
                        data.children?.find((c) => c.meta.type === "PROJECT_PERSON_SET_LIST") as
                            | ProjectDataChildPeopleSetList
                            | undefined
                    )?.children?.find((c) => c.meta.path === shortListRef);

                    return { company, people, shortList };
                });

                const { crm_vacancy, functional_balance, individual_positioning } = d.data;
                const isChart1Invalid = Object.values(functional_balance).every((val) => val !== undefined);
                const isChart2Invalid = Object.values(individual_positioning).every((val) => val !== undefined);
                const shouldDisabled = crm_vacancy === null && isChart1Invalid && isChart2Invalid;

                return {
                    id: d.data.id,
                    name: d.data.name || "",
                    relatedLists: {
                        company: relatedLists?.map((e) => e.company?.meta?.path).filter((el) => el !== undefined) || [],
                        people: relatedLists?.map((e) => e.people?.meta?.path).filter((el) => el !== undefined) || [],
                        shortList:
                            relatedLists?.map((e) => e.shortList?.meta?.path).filter((el) => el !== undefined) || [],
                    },
                    shouldHighlight: false,
                    shouldHide: false,
                    isCompleted: false,
                    isDisabled: shouldDisabled,
                    activityLog: d.avatar_history,
                    data: d.data,
                    meta: {
                        relatedInsight,
                        pathId: extractIdFromPath(d.meta.path),
                        projectId: data.data.id,
                        companies_count: d.meta.companies_count || 0,
                        shortlist_count: d.meta.shortlist_count || 0,
                        people_count: d.meta.people_count || 0,
                    },
                };
            }) || []
    );
};

export const getSearchAIList = (data: ProjectDataResponse) => {
    if (isEmpty(data)) {
        return [];
    }

    const search = <T extends searchAIAllChildren>(list: T[], metaType: string) => {
        return list.find((data) => data.meta.type === metaType);
    };

    const searchAIList = data.children.find((c) => c.meta.type === "PROJECT_SEARCH_AI_LIST") as
        | ProjectDataChildSearchAIList
        | undefined;

    return (
        searchAIList?.children
            ?.filter((d) => d.avatar)
            ?.map((d) => {
                const briefRef = (
                    d.children?.find((l) => l.meta.type === "PROJECT_BRIEF_REF") as searchAIChildBrief | undefined
                )?.ref?.path;

                const relatedBrief = (
                    data.children?.find((c) => c.meta.type === "PROJECT_BRIEF_LIST") as
                        | ProjectDataChildBriefSetList
                        | undefined
                )?.children?.find((insight) => insight.meta.path === briefRef)?.data;

                const relatedInsightPath = (
                    data.children?.find((data) => data.meta.type === "PROJECT_BRIEF_LIST") as
                        | ProjectDataChildBriefSetList
                        | undefined
                )?.children
                    ?.find((data) => data.meta.path === briefRef)
                    ?.children?.find((s) => s.meta.type === "PROJECT_INSIGHT_REF")?.ref?.path;

                const companiesRef = (
                    search(d?.children, "PROJECT_SEARCH_COMPANIES") as searchAIChildSearchCompanies | undefined
                )?.children?.find((c) => c.meta.type === "PROJECT_COMPANY_SET_REF_B")?.ref?.path;
                const peopleRef = (
                    d?.children?.find((data) => data.meta.type === "PROJECT_PERSON_SET_REF_A") as
                        | searchAIChildPersonA
                        | undefined
                )?.ref?.path;
                const shortListRef = (
                    d?.children?.find((data) => data.meta.type === "PROJECT_PERSON_SET_REF_B") as
                        | searchAIChildPersonB
                        | undefined
                )?.ref?.path;

                const companiesList = (
                    data.children?.find((child) => child.meta.type === "PROJECT_COMPANY_SET_LIST") as
                        | ProjectDataChildCompanySetList
                        | undefined
                )?.children?.find((c) => c.meta.path === companiesRef);

                const longList = (
                    data.children?.find((child) => child.meta.type === "PROJECT_PERSON_SET_LIST") as
                        | ProjectDataChildPeopleSetList
                        | undefined
                )?.children?.find((c) => c.meta.path === peopleRef);

                const shortList = (
                    data.children?.find((child) => child.meta.type === "PROJECT_PERSON_SET_LIST") as
                        | ProjectDataChildPeopleSetList
                        | undefined
                )?.children?.find((c) => c.meta.path === shortListRef);

                const createdOn = d.avatar_history.find((ah) => ah.activity === "Created");
                const searchAiHistory = d.avatar_history || [];
                const searchAiLonglistHistory = longList?.avatar_history || [];
                const searchAiShortlistHistory = shortList?.avatar_history || [];
                const searchAiCompanyHistory = companiesList?.avatar_history || [];
                const allHistory = [
                    ...searchAiHistory,
                    ...searchAiLonglistHistory,
                    ...searchAiShortlistHistory,
                    ...searchAiCompanyHistory,
                ];

                return {
                    id: d.data.id,
                    name: d.data.name || "",
                    shouldHighlight: false,
                    shouldHide: false,
                    isCompleted: false,
                    isDisabled: false,
                    activityLog: d.avatar_history,
                    meta: {
                        createdOn,
                        lastUpdatedOn: allHistory.sort((a, b) =>
                            compareDesc(new Date(a.datetime_utc || ""), new Date(b.datetime_utc || ""))
                        )[0],
                        collaborators: uniqBy(
                            allHistory.filter((ah) => ah.name !== createdOn?.name),
                            "name"
                        ),
                        relatedBrief,
                        relatedCompanyListId: companiesList?.data.id || 0,
                        relatedLongListId: longList?.data.id || 0,
                        relatedShortListId: shortList?.data.id || 0,
                        relatedInsightId: relatedInsightPath && extractIdFromPath(relatedInsightPath),
                        pathId: extractIdFromPath(d.meta.path),
                        projectId: data.data.id,
                        ref: data.meta.path,
                        companies_count: companiesList?.meta?.children_count || 0,
                        shortlist_count: shortList?.meta?.children_count || 0,
                        people_count: longList?.meta?.children_count || 0,
                    },
                };
            }) || []
    );
};
